import { useMemo } from 'react';
import { produce, type Draft } from 'immer';
import { calcSelfFundingProjectedCostCoverage, calculateMonthsBetweenYears } from '@shared';

export interface UseSelfFundingProjectedValueOptions {
  ltcAtYear: number;
  yearsTillLtc: number;
  /** Months of care used to include monthly income in projected value (from client.allPhaseCosts). */
  allPhaseCareMonthsNeeded?: number;
  //using it when available for the "age at LTC" label
  ltcAtAge?: number | null;
  //Fallback when ltcAtAge is not available: compute age from birth date and ltcAtYear.
  clientBirthDate?: string | Date | null;
}

/**
 * Computes projected value and age-at-LTC for a self-funding state using the same
 * calculation as the server (calcSelfFundingProjectedCostCoverage). Use for live
 * preview in SelfFundingDetailsModal and AccountCard.
 */
export function useSelfFundingProjectedValue(
  state: Partial<SelfFundingSource> | null | undefined,
  options: UseSelfFundingProjectedValueOptions,
): { projectedValue: number | null; ageAtLtc: number | null } {
  const {
    ltcAtYear,
    yearsTillLtc,
    allPhaseCareMonthsNeeded = 0,
    ltcAtAge: ltcAtAgeFromInference,
    clientBirthDate,
  } = options;

  return useMemo(() => {
    if (!state) {
      return { projectedValue: null, ageAtLtc: null };
    }

    const existingAssetsValue = state.existingAssetsValue;
    const annualRateOfReturn = state.annualRateOfReturn;
    const gainsTaxRate = state.gainsTaxRate;

    const canCompute =
      existingAssetsValue != null && annualRateOfReturn != null && gainsTaxRate != null;

    let projectedValue: number | null = null;
    if (canCompute) {
      const contributionStartYear = new Date().getFullYear();
      const contributionEndYear = ltcAtYear;
      const contributionMonthCount =
        calculateMonthsBetweenYears(contributionStartYear, contributionEndYear) || 1;

      const selfFundingSource: SelfFundingSource = {
        hasSelfFunding: true,
        existingAssetsValue: existingAssetsValue ?? null,
        monthlyContributionAmount: state.monthlyContributionAmount ?? null,
        annualRateOfReturn: annualRateOfReturn ?? null,
        gainsTaxRate: gainsTaxRate ?? null,
        contributionStartYear,
        contributionEndYear,
        contributionMonthCount,
        monthlyIncome: state.monthlyIncome ?? null,
      };

      const partialClient = {
        fundingSources: { selfFunding: selfFundingSource },
        allPhaseCosts: { allPhaseCareMonthsNeeded },
        appliedInferenceSet: { yearsTillLtc: Math.max(yearsTillLtc, 0) },
      } as Partial<Client>;

      const result = produce(partialClient, (draft: Draft<Client>) => {
        calcSelfFundingProjectedCostCoverage(draft);
      }) as Partial<Client>;

      projectedValue = result.fundingSources?.selfFunding?.selfFundingTotalValue ?? null;
    }

    let ageAtLtc: number | null = null;
    if (ltcAtAgeFromInference != null && Number.isFinite(ltcAtAgeFromInference)) {
      ageAtLtc = ltcAtAgeFromInference;
    } else if (clientBirthDate) {
      try {
        const birth =
          typeof clientBirthDate === 'string' ? new Date(clientBirthDate) : clientBirthDate;
        const birthYear = birth.getFullYear();
        if (!Number.isNaN(birthYear)) {
          ageAtLtc = ltcAtYear - birthYear;
        }
      } catch {
        // leave ageAtLtc null
      }
    }

    return { projectedValue, ageAtLtc };
  }, [
    state?.existingAssetsValue,
    state?.monthlyContributionAmount,
    state?.annualRateOfReturn,
    state?.gainsTaxRate,
    state?.monthlyIncome,
    ltcAtYear,
    yearsTillLtc,
    allPhaseCareMonthsNeeded,
    ltcAtAgeFromInference,
    clientBirthDate,
  ]);
}
